import { FC, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchUser } from "../../http/types/users";
import { useUsersApi } from "../../http/useUsers";
import { PaginationQuery } from "../../types";
import { formatUtcDay } from "../../utils";
import { FlexContent, TableCell, TableHeadCell, TableRow, useDebounce } from "@rothoblaas/design-system";
import Table from "../ui/Table";

// The stored users, searchable and paged.
const AdminUsersList: FC = () => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const navigate = useNavigate();

  const debouncedValue = useDebounce(filters.state.q);

  const usersApi = useUsersApi();

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = usersApi.getAdminUsers(query, aborter.current.signal);

  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  return (
    <FlexContent padding>
      <Table<SearchUser>
        filters={filters}
        search={{
          placeholder: i18n.translation.users.search,
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <TableRow>
            <TableHeadCell align="left">{i18n.translation.common.email}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.common.lastAccess}</TableHeadCell>
          </TableRow>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <TableCell>
              <Link onClick={(e) => e.stopPropagation()} to={item.id}>
                {item.email}
              </Link>
            </TableCell>
            <TableCell>
              {item.firstName} {item.lastName}
            </TableCell>
            <TableCell>{!!item.lastAccess && formatUtcDay(item.lastAccess, auth.identity?.language)}</TableCell>
          </>
        )}
        onRowClick={(item) => navigate(`/admin/users/${item.id}`)}
        total={data?.total}
        error={!!error}
      />
    </FlexContent>
  );
};

export default AdminUsersList;
